import {CmsServiceV13Spi, NbabInfo} from '@volkswagen-onehub/cms-service';
import {autorun} from 'mobx';
import {NextBestActionListModel} from '../../../../generated/core';
import {Registry} from '../../di/Registry';
import {ContentStore} from '../../../context/content/ContentStore';
import {NbabApiStore} from '../../../context/nbab/NbabApiStore';
import {NbabType} from '../../../types/global';
import {isInBrowser} from '../../../utils/browser/isInBrowser';
import {PersonalizedNbabStore} from '../../../context/nbab/PersonalizedNbabStore';
import {Environment} from '../../di/annotations';
import {Logger} from '../../../context/logger/Logger';

export type QuerySelectorFn = (query: string) => Element | undefined;

export function getNbabInfoFactory(
    registry: Registry,
    logger: Logger,
    querySelector: QuerySelectorFn
): {
    getNbabInfo: CmsServiceV13Spi['getNbabInfo'];
    listenToNbabInfo: CmsServiceV13Spi['listenToNbabInfo'];
    cleanup: CmsServiceV13Spi['cleanup'];
} {
    if (!isInBrowser()) {
        return {
            listenToNbabInfo: () => {
                return () => {};
            },
            getNbabInfo() {
                return undefined;
            },
            cleanup() {}
        };
    }
    const getNbabInfo = (consumerId: string) => {
        // content store provides the configured nbabs for the current page
        const contentStore: ContentStore =
            registry.getSingleton('ContentStore');
        // nbab api store provides the layout of the nbab
        const nbabApiStore: NbabApiStore =
            registry.getSingleton('NbabApiStore');
        // need to also take personalized actions into account
        const personalizedNbabStore: PersonalizedNbabStore =
            registry.getSingleton('PersonalizedNbabStore');

        const nbabType = nbabApiStore.nbabType;
        const nextBestActionDataModel =
            contentStore.getCurrentPageRootModel()?.nextBestActionDataModel;
        if (!nextBestActionDataModel) {
            return undefined;
        }
        const pageModel = nextBestActionDataModel.pageModel;

        // get the section model for the feature app if any
        const sectionModel = Object.values(
            nextBestActionDataModel.sectionModels
        ).find(s => s.faConsumerId === consumerId);
        return createNbabInfo(
            sectionModel,
            pageModel,
            personalizedNbabStore.personalizedActions.length,
            querySelector,
            nbabType,
            consumerId
        );
    };
    const listeners: {listener: () => void; consumerId: string}[] = [];

    const listenToNbabInfo = (consumerId: string, listener: () => void) => {
        listeners.push({listener, consumerId});
        return () => {
            const index = listeners.findIndex(l => l.listener === listener);
            if (index >= 0) {
                listeners.splice(index, 1);
            }
        };
    };

    const cleanup = (consumerId: string) => {
        const index = listeners.findIndex(l => l.consumerId === consumerId);
        if (index >= 0) {
            listeners.splice(index, 1);
        }
    };

    const init = () => {
        const personalizedNbabStore: PersonalizedNbabStore =
            registry.getSingleton('PersonalizedNbabStore');
        autorun(() => {
            // read props to start observation but don't do anything with it
            const actionCount =
                personalizedNbabStore.personalizedActions.length;
            console.debug(actionCount);
            listeners.forEach(l => {
                try {
                    l.listener();
                } catch (e) {
                    logger.nbab.error('error in nbab info layer', e);
                }
            });
        });
    };
    registry.onInit((env: Environment) => {
        if (env === 'client') init();
    });
    return {listenToNbabInfo, getNbabInfo, cleanup};
}
export const PAGE_NBAB_ID = 'page';

/**
 * get the nbab info for the feature app with given consumerIdbased on all given information
 * @param sectionModel
 * @param pageModel
 * @param personalizedActionLength
 * @param nbabType
 * @param faConsumerId
 * @returns
 */
function createNbabInfo(
    sectionModel: NextBestActionListModel | undefined,
    pageModel: NextBestActionListModel,
    personalizedActionLength: number,
    querySelector: QuerySelectorFn,
    nbabType?: NbabType,
    faConsumerId?: string
): NbabInfo | undefined {
    let nbabId = PAGE_NBAB_ID;
    if (!nbabType || nbabType === 'none') {
        return undefined;
    }

    if (
        sectionModel &&
        (sectionModel.nbabDisabled || sectionModel.actions.length === 0)
    ) {
        return undefined;
    }

    if (sectionModel && faConsumerId) {
        nbabId = faConsumerId;
    }

    if (
        !sectionModel &&
        pageModel.actions.length === 0 &&
        personalizedActionLength === 0
    ) {
        return undefined;
    }

    return {
        nbabType,
        get boundingElement() {
            return querySelector(`[data-nbab-id='${nbabId}']`) || undefined;
        }
    };
}
